/**
 * ReceiptDrawer Component
 * Create/Edit/View Receipt form for Purchase Orders
 * BuilderBel Construction Project Management System
 */

import { yupResolver } from '@hookform/resolvers/yup';
import { AlertCircle, Check, ChevronDown, ChevronRight, Package, Truck, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useFieldArray, useForm, Resolver } from 'react-hook-form';
import * as Yup from 'yup';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';
import {
  useCreateReceiptMutation,
  useUpdateReceiptMutation,
  useConfirmReceiptMutation,
} from '../../services/purchaseOrderApi';
import type {
  PurchaseOrder,
  Receipt,
  ReceiptItemCondition,
  CreateReceiptRequest,
  UpdateReceiptRequest,
} from '../../types/purchaseOrder';
import { DatePicker, Input, TextArea, Dropdown, NumberInput } from '../forms';

// Props interface
export interface ReceiptDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrder: PurchaseOrder;
  receipt?: Receipt | null;
  mode: 'create' | 'edit' | 'view';
  onSuccess?: () => void;
}

// Receipt item form data
interface ReceiptItemFormData {
  lineItemId: string;
  description: string;
  quantityOrdered: number;
  quantityReceived: number;
  quantityPrevious: number;
  condition: ReceiptItemCondition;
  notes: string;
}

// Form data type
interface ReceiptFormData {
  receiptDate: string;
  items: ReceiptItemFormData[];
  packingSlip: string;
  deliveryNote: string;
  carrier: string;
  trackingNumber: string;
  signedBy: string;
  notes: string;
}

// Condition options
const CONDITION_OPTIONS = [
  { value: 'good', label: 'Good' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'partial', label: 'Partial' },
];

// Validation schema
const receiptSchema = Yup.object().shape({
  receiptDate: Yup.string().required('Receipt date is required'),
  items: Yup.array()
    .of(
      Yup.object().shape({
        lineItemId: Yup.string().required(),
        description: Yup.string().required(),
        quantityOrdered: Yup.number().min(0),
        quantityReceived: Yup.number().min(0, 'Quantity must be positive').required('Required'),
        quantityPrevious: Yup.number().min(0),
        condition: Yup.string().oneOf(['good', 'damaged', 'partial']),
        notes: Yup.string(),
      })
    )
    .min(1, 'At least one item is required'),
  packingSlip: Yup.string().max(100, 'Max 100 characters'),
  deliveryNote: Yup.string().max(1000, 'Max 1000 characters'),
  carrier: Yup.string().max(200, 'Max 200 characters'),
  trackingNumber: Yup.string().max(100, 'Max 100 characters'),
  signedBy: Yup.string().max(200, 'Max 200 characters'),
  notes: Yup.string().max(2000, 'Max 2000 characters'),
});

export const ReceiptDrawer: React.FC<ReceiptDrawerProps> = ({
  isOpen,
  onClose,
  purchaseOrder,
  receipt,
  mode,
  onSuccess,
}) => {
  const projectId = useCurrentProjectId();
  const toast = useToast();
  const [expandedSections, setExpandedSections] = useState({
    items: true,
    delivery: false,
    notes: false,
  });

  const [createReceipt, { isLoading: isCreating }] = useCreateReceiptMutation();
  const [updateReceipt, { isLoading: isUpdating }] = useUpdateReceiptMutation();
  const [confirmReceipt, { isLoading: isConfirming }] = useConfirmReceiptMutation();

  const isReadOnly = mode === 'view';
  const isSubmitting = isCreating || isUpdating || isConfirming;

  // Default form values
  const defaultValues: ReceiptFormData = useMemo(() => {
    if (receipt) {
      return {
        receiptDate: receipt.receiptDate?.split('T')[0] || new Date().toISOString().split('T')[0],
        items: receipt.items.map(item => ({
          lineItemId: item.lineItemId,
          description: item.description,
          quantityOrdered: item.quantityOrdered,
          quantityReceived: item.quantityReceived,
          quantityPrevious: item.quantityPrevious,
          condition: item.condition,
          notes: item.notes || '',
        })),
        packingSlip: receipt.packingSlip || '',
        deliveryNote: receipt.deliveryNote || '',
        carrier: receipt.carrier || '',
        trackingNumber: receipt.trackingNumber || '',
        signedBy: receipt.signedBy || '',
        notes: receipt.notes || '',
      };
    }

    // Create mode - populate from PO line items
    return {
      receiptDate: new Date().toISOString().split('T')[0],
      items: purchaseOrder.lineItems.map(item => ({
        lineItemId: item._id,
        description: item.description,
        quantityOrdered: item.quantity,
        quantityReceived: 0,
        quantityPrevious: 0, // TODO: Calculate from previous receipts
        condition: 'good' as ReceiptItemCondition,
        notes: '',
      })),
      packingSlip: '',
      deliveryNote: '',
      carrier: '',
      trackingNumber: '',
      signedBy: '',
      notes: '',
    };
  }, [receipt, purchaseOrder]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ReceiptFormData>({
    resolver: yupResolver(receiptSchema) as Resolver<ReceiptFormData>,
    defaultValues,
  });

  const { fields } = useFieldArray({
    control,
    name: 'items',
  });

  // Reset form when receipt changes
  useEffect(() => {
    reset(defaultValues);
  }, [receipt, defaultValues, reset]);

  // Handle form submission
  const onSubmit = async (data: ReceiptFormData) => {
    if (!projectId) {
      toast.error('No project selected');
      return;
    }

    try {
      const requestData: CreateReceiptRequest | UpdateReceiptRequest = {
        receiptDate: data.receiptDate,
        items: data.items.map(item => ({
          lineItemId: item.lineItemId,
          description: item.description,
          quantityOrdered: item.quantityOrdered,
          quantityReceived: item.quantityReceived,
          quantityPrevious: item.quantityPrevious,
          condition: item.condition,
          notes: item.notes || undefined,
        })),
        packingSlip: data.packingSlip || undefined,
        deliveryNote: data.deliveryNote || undefined,
        carrier: data.carrier || undefined,
        trackingNumber: data.trackingNumber || undefined,
        signedBy: data.signedBy || undefined,
        notes: data.notes || undefined,
      };

      if (mode === 'create') {
        await createReceipt({
          projectId,
          purchaseOrderId: purchaseOrder._id,
          data: requestData as CreateReceiptRequest,
        }).unwrap();
        toast.success('Receipt created successfully');
      } else if (mode === 'edit' && receipt) {
        await updateReceipt({
          projectId,
          purchaseOrderId: purchaseOrder._id,
          receiptId: receipt._id,
          data: requestData as UpdateReceiptRequest,
        }).unwrap();
        toast.success('Receipt updated successfully');
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Receipt save error:', error);
      toast.error('Failed to save receipt');
    }
  };

  // Handle confirm
  const handleConfirm = async () => {
    if (!projectId || !receipt) return;

    try {
      await confirmReceipt({
        projectId,
        purchaseOrderId: purchaseOrder._id,
        receiptId: receipt._id,
      }).unwrap();
      toast.success('Receipt confirmed successfully');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Receipt confirm error:', error);
      toast.error('Failed to confirm receipt');
    }
  };

  // Toggle section
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[60%] min-w-[600px] animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <Package className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {mode === 'create'
                    ? 'Create Receipt'
                    : mode === 'edit'
                      ? 'Edit Receipt'
                      : `Receipt ${receipt?.receiptNumber || ''}`}
                </h2>
                <p className="text-sm text-gray-500">PO: {purchaseOrder.poNumber}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto">
            <div className="px-8 py-6 space-y-6">
              {/* Receipt Date */}
              <div className="grid grid-cols-2 gap-4">
                <Controller
                  name="receiptDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Receipt Date"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.receiptDate?.message}
                      disabled={isReadOnly}
                      required
                    />
                  )}
                />
                {receipt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <span
                      className={`inline-flex px-3 py-2 rounded-lg text-sm font-medium ${
                        receipt.status === 'confirmed'
                          ? 'bg-green-100 text-green-800'
                          : receipt.status === 'void'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {receipt.status.charAt(0).toUpperCase() + receipt.status.slice(1)}
                    </span>
                  </div>
                )}
              </div>

              {/* Items Section */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('items')}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <Package className="h-4 w-4 text-gray-500" />
                    <span className="font-medium text-gray-900">Line Items</span>
                    <span className="text-sm text-gray-500">({fields.length} items)</span>
                  </div>
                  {expandedSections.items ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.items && (
                  <div className="p-4 space-y-4">
                    {fields.map((field, index) => (
                      <div
                        key={field.id}
                        className="p-4 bg-gray-50 rounded-lg space-y-3 border border-gray-200"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{field.description}</p>
                            <p className="text-sm text-gray-500">
                              Ordered: {field.quantityOrdered} | Previous: {field.quantityPrevious}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <Controller
                            name={`items.${index}.quantityReceived`}
                            control={control}
                            render={({ field: inputField }) => (
                              <NumberInput
                                label="Qty Received"
                                value={inputField.value}
                                onChange={inputField.onChange}
                                min={0}
                                error={errors.items?.[index]?.quantityReceived?.message}
                                disabled={isReadOnly}
                              />
                            )}
                          />
                          <Controller
                            name={`items.${index}.condition`}
                            control={control}
                            render={({ field: selectField }) => (
                              <Dropdown
                                label="Condition"
                                options={CONDITION_OPTIONS}
                                value={selectField.value}
                                onChange={selectField.onChange}
                                disabled={isReadOnly}
                              />
                            )}
                          />
                          <Controller
                            name={`items.${index}.notes`}
                            control={control}
                            render={({ field: inputField }) => (
                              <Input
                                label="Notes"
                                value={inputField.value}
                                onChange={inputField.onChange}
                                disabled={isReadOnly}
                                placeholder="Optional notes"
                              />
                            )}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Delivery Section */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('delivery')}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <Truck className="h-4 w-4 text-gray-500" />
                    <span className="font-medium text-gray-900">Delivery Information</span>
                  </div>
                  {expandedSections.delivery ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.delivery && (
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Controller
                        name="packingSlip"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Packing Slip #"
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.packingSlip?.message}
                            disabled={isReadOnly}
                            placeholder="Enter packing slip number"
                          />
                        )}
                      />
                      <Controller
                        name="carrier"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Carrier"
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.carrier?.message}
                            disabled={isReadOnly}
                            placeholder="e.g., UPS, FedEx"
                          />
                        )}
                      />
                      <Controller
                        name="trackingNumber"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Tracking Number"
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.trackingNumber?.message}
                            disabled={isReadOnly}
                            placeholder="Enter tracking number"
                          />
                        )}
                      />
                      <Controller
                        name="signedBy"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="Signed By"
                            value={field.value}
                            onChange={field.onChange}
                            error={errors.signedBy?.message}
                            disabled={isReadOnly}
                            placeholder="Name of receiver"
                          />
                        )}
                      />
                    </div>
                    <Controller
                      name="deliveryNote"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label="Delivery Note"
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.deliveryNote?.message}
                          disabled={isReadOnly}
                          rows={3}
                          placeholder="Enter delivery notes"
                        />
                      )}
                    />
                  </div>
                )}
              </div>

              {/* Notes Section */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('notes')}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-4 w-4 text-gray-500" />
                    <span className="font-medium text-gray-900">Notes</span>
                  </div>
                  {expandedSections.notes ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.notes && (
                  <div className="p-4">
                    <Controller
                      name="notes"
                      control={control}
                      render={({ field }) => (
                        <TextArea
                          label="General Notes"
                          value={field.value}
                          onChange={field.onChange}
                          error={errors.notes?.message}
                          disabled={isReadOnly}
                          rows={4}
                          placeholder="Enter any additional notes"
                        />
                      )}
                    />
                  </div>
                )}
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="border-t border-gray-100 px-8 py-5 bg-gray-50 flex items-center justify-between">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              {isReadOnly ? 'Close' : 'Cancel'}
            </button>
            <div className="flex items-center space-x-3">
              {mode === 'view' && receipt?.status === 'draft' && (
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
                >
                  <Check className="h-4 w-4" />
                  <span>Confirm Receipt</span>
                </button>
              )}
              {!isReadOnly && (
                <button
                  type="submit"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSubmitting || !isDirty}
                  className="px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting
                    ? 'Saving...'
                    : mode === 'create'
                      ? 'Create Receipt'
                      : 'Save Changes'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ReceiptDrawer;
